"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DomainServices = void 0;
var _config = require("./config");
var _Fetcher = require("./Fetcher");
var _utils = require("./utils");
var _utils2 = require("@site/utils");
/* eslint-disable max-len */

/** @module DomainServices */

const getQStringValue = ({
  extraParams
}) => {
  const qsValues = new URLSearchParams(extraParams).toString();
  return qsValues !== '' ? `?${qsValues}` : '';
};

/** packages/services/domain-services/src/DomainServices.js
 * Domain Services Javascript Library for Domain Find API calls
 * @module @site/domain-services
 * @example
 * const domainServices = new DomainServices({
 *   fetchEntity: window.fetch,
 *   apiKey: 'TEST_DPP_SEARCH',
 *   plid: '1',
 *   env: 'dev',
 *   market: 'en-US'
 * });
 *
 * // use async/await
 * const data = await domainServices.findExact({ 'abcdef.com' })
 *
 * // or with a Promise...
 * domainServices.findExactPromise({ 'abcdef.com' }).then(response => {
 *   response.json().then(data => {
 *      setFindExactData('findExact', data);
 *   });
 * });
 */

class DomainServices {
  _validateConstructorParams(env, apiKey, fetchEntity) {
    if (!_config.config.isValidEnv(env)) {
      throw new Error('invalid env given use prod, dev, or test');
    }
    if (apiKey === '') {
      throw new Error('apiKey is required');
    }
    if (!fetchEntity) {
      throw new Error('a fetchEntity must be provided. Use window.fetch or node-fetch, etc');
    }
  }
  _createInternalConfig(params) {
    const {
      env,
      plid,
      market,
      resellerBaseOverride,
      isServerCall,
      fetchEntity,
      logger,
      apiKey
    } = params;
    return {
      config: _config.config,
      logger,
      market,
      // eslint-disable-next-line new-cap
      fetcher: (0, _Fetcher.Fetcher)(fetchEntity),
      apiKey,
      env,
      plid,
      paths: _config.config.getPaths(env),
      isReseller: _config.config.isReseller(plid),
      host: _config.config.getHosts(env, plid, market, {
        resellerBaseOverride,
        isServerCall
      }),
      sapiHost: _config.config.getSapiHost(env, plid, resellerBaseOverride),
      genAiHost: _config.config.getGenAiHost(env, plid, resellerBaseOverride),
      uipHost: _config.config.getUipHost(env),
      genAiBatchHost: _config.config.getGenAiBatchHost(env),
      auctionsHost: _config.config.getAuctionsHost(env, plid, market),
      dccDomainsHost: _config.config.getDccDomainsHost(env),
      llcAvailCheckApiHost: _config.config.getLLCAvailCheckApiHost(env),
      findAftermarketHost: _config.config.getFindAftermarketHost(env),
      productGraphHost: _config.config.getProductGraphHost(env),
      tokenApiHost: _config.config.getTokenApiHost(env),
      entourageHost: _config.config.getEntourageHost(env)
    };
  }

  /**
   * @param {DomainServicesOptions} ctorargs object containing...
   * @param {fetch}  ctorargs.fetchEntity instance of a fetch interface that follows the fetch spec
   * @param {string} ctorargs.apiKey apiKey which ends up as key on the querystring to the find apis
   * @param {string} [ctorargs.env = 'prod'] valid values are dev, test, prod
   * @param {string} [ctorargs.plid = '1'] numeric value representing the registrar ('1' is GoDaddy)
   * @param {string} [ctorargs.market = 'en-US'] string representing the market; ie. en-US, fr-CA, fr-FR, etc
   * @param {string} [ctorargs.resellerBaseOverride] [Optional] string value for a non-secureserver.net reseller
   * @param {boolean} [ctorargs.isServerCall = false] [Optional] indicates if this is a server-side call
   * @param {function} [ctorargs.logger] function that logs are sent to using interface (log) => {}
   * @constructor
   */

  constructor({
    fetchEntity = global.fetch,
    apiKey = '',
    env = 'prod',
    plid = '1',
    market = 'en-US',
    resellerBaseOverride,
    isServerCall = false,
    logger = () => {}
  }) {
    this._validateConstructorParams(env, apiKey, fetchEntity);
    if (resellerBaseOverride) {
      resellerBaseOverride = (0, _utils.validateAndFixResellerBaseUrl)(resellerBaseOverride);
    }
    this._internal = this._createInternalConfig({
      env,
      plid,
      market,
      resellerBaseOverride,
      isServerCall,
      fetchEntity,
      logger,
      apiKey
    });
    ['commonSearchParams', 'getUrl', 'getExactUrl', 'getAddToCartTrackUrl', 'getDivinerUrl', 'getSpinsUrl', 'getCrossSellUrl', 'getContentUrl', 'getFindBundlesUrl', 'getSmartBundlesUrl', 'getSmartExactUrl', 'getSapiCartDomainsUrl', 'getBasketUrl', 'getBasket', 'getBulkUrl', 'getContent', 'getDomainSearchComponentBFFUrl', 'getMerchRecommendUrl', 'getFindAftermarketAuctionsUrl', 'findAftermarketAuctionsPromise', 'findMerchRecommendPromise', 'findMerchRecommend', 'findExact', 'findAddToCartTrack', 'findExactPromise', 'findDivinerPromise', 'genAiLogoGenerationPromise', 'genAiLogoBatchGenerationBatchIdPromise', 'genAiLogoBatchGenerationRequestUrlPromise', 'genAiLogoBatchGenerationLogoUrlPromise', 'findSpins', 'findSpinsPromise', 'findCrossSell', 'findCrossSellPromise', 'findBundles', 'findBundlesPromise', 'findSmartBundles', 'findSmartBundlesPromise', 'findSmartExactPromise', 'findBulkOptions', 'findBulk', 'findBulkPromise', 'sapiCartDomains', 'sapiCartDomainsPromise', 'sapiCartPackages', 'sapiCartPackagesPromise', 'llcAvailCheckPromise', 'getSapiCartPackagesUrl', 'getDeepIndustryUrl', 'getDeepIndustryPromise', 'getQueryCompleteApiUrl', 'getQueryCompletePromise', 'getUipApiPromise', 'getUipApiUrl', 'getDomainSearchComponentBFFApi', 'getDomainSearchComponentBFFApiPromise', 'getDomainSuggestionsUrl', 'getDomainSuggestionsPromise', 'getDccDomainsGetBody', 'getDccDomainsGetUrl', 'getDccDomainsGetPromise', 'getProductGraphUrl', 'findProductGraphPromise', 'getTokenApiPromise'].forEach(method => this[method] = this[method].bind(this));
  }

  /**
   * @property {boolean} isReseller utility field that determines if the current plid is not '1'
   */
  get isReseller() {
    return this._internal.isReseller;
  }

  /**
   * @property {string} host domain instance value that is set using the env, plid and market
   */
  get host() {
    return this._internal.host;
  }

  /**
   * @property {string} sapiHost domain instance value that is set using the env, plid and market
   */
  get sapiHost() {
    return this._internal.sapiHost;
  }

  /**
   * @property {string} genAi domain instance value that is set using the env, plid and market
   */
  get genAiHost() {
    return this._internal.genAiHost;
  }

  /**
   * @property {string} genAiBatchHost instance value that is set using the env.
   */
  get genAiBatchHost() {
    return this._internal.genAiBatchHost;
  }

  /**
   * @property {string} findAftermarketHost domain instance value that is set using the env
   */
  get findAftermarketHost() {
    return this._internal.findAftermarketHost;
  }

  /**
   * @property {string} entourageHost domain instance value that is set using the env
   */
  get entourageHost() {
    return this._internal.entourageHost;
  }

  /**
   * @property {string} llcAvailCheckApiHost domain instance value that is set using the env
   */
  get llcAvailCheckApiHost() {
    return this._internal.llcAvailCheckApiHost;
  }

  /**
   * @property {string} uipHost domain instance value that is set using the env
   */
  get uipHost() {
    return this._internal.uipHost;
  }

  /**
   * @property {string} auctionsHost domain instance value that is set using the env, plid and market
   */
  get auctionsHost() {
    return this._internal.auctionsHost;
  }

  /**
   * @property {string} dccDomainsHost domain instance value that is set using the env
   */
  get dccDomainsHost() {
    return this._internal.dccDomainsHost;
  }

  /**
   * @property {string} productGraphHost domain instance value that is set using the env
   */
  get productGraphHost() {
    return this._internal.productGraphHost;
  }

  /**
   * @property {string} tokenApiHost domain instance value that is set using the env
   */
  get tokenApiHost() {
    return this._internal.tokenApiHost;
  }

  /**
   * @property {object} paths paths object containng the root paths for each api call using environment {env}
   */
  get paths() {
    return this._internal.paths;
  }

  /**
   * @property {string} plid this configures the reseller used; default as '1' for GoDaddy
   */
  get plid() {
    return this._internal.plid;
  }

  /**
   * @property {object} config configuration exposed for your convenience (see config.js)
   */
  get config() {
    return this._internal.config;
  }

  /**
   * @property {function} logger function that function that logs are sent to
   */
  get logger() {
    return this._internal.logger;
  }

  /**
   * @property {string} market string reprenting the market; ie. en-US, fr-CA, fr-FR, etc
   */
  get market() {
    return this._internal.market;
  }

  /**
   * @property {Fetcher} fetcher fetcher interface for encapsulating the fetch mechanism.
   */
  get fetcher() {
    return this._internal.fetcher;
  }

  /**
   * @property {string} apiKey string reprenting the market; ie. en-US, fr-CA, fr-FR, etc
   */
  get apiKey() {
    return this._internal.apiKey;
  }

  /**
   * @property {string} env environment; valid values are dev, test, prod
   */
  get env() {
    return this._internal.env;
  }

  /**
   * Utility method that takes query and extraParams and returns an object that contains the querystring values
   * @param {string} query options object containing...
   * @param {object} [extraParams] Record<string,string> containing key/values used for querystring params
   * @returns {object} a Record<string,string> of the querystring params
   */
  commonSearchParams(query = '', extraParams = {}) {
    const {
      apiKey,
      plid,
      isReseller
    } = this;
    const searchParams = {
      key: extraParams.apiKey || apiKey,
      req_id: extraParams.req_id || Date.now()
    };
    if (query && query.length > 0) {
      searchParams.q = query;
    }
    if (isReseller) {
      searchParams.plid = plid;
    }
    return searchParams;
  }

  /**
   * Utility method that returns the url for a given api call provided query, extraParms and path
   * @param {object} args args object containing...
   * @param {string} args.query domain query value used to search
   * @param {string} args.path string that contains the path of the API to call
   * @param {object} [args.extraParams] Record<string,string> containing key/values used for querystring params
   * @returns {string} full url for Api call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const url = domainServices.getUrl({ query, extraParams, path: domainServices.paths.exactGet })
   */
  getUrl({
    query = '',
    path = '',
    extraParams = {},
    keepApiKey = false,
    queryParams = {}
  } = {}) {
    const searchParams = this.commonSearchParams(query, extraParams);
    const urlParams = {
      ...extraParams,
      ...searchParams,
      ...queryParams
    };
    !keepApiKey && delete urlParams.apiKey;
    const qstring = getQStringValue({
      extraParams: urlParams
    });
    return `${this.host}${path}${qstring}`;
  }

  /**
   * SapiCartPackagesUrl returns the url for a given api call provided sapiOptions
   * @param {object} args args object containing...
   * @param {string} args.packageId target packageId
   * @param {object} [args.extraParams={}] extra parameters that go on the query string
   * @returns {string} full url for sapiCartPakcagesUrl call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const packageId = getPackageId();
   * const cartPackageUrl = domainServices.getSapiCartPackagesUrl({ packageId });
   */
  getSapiCartPackagesUrl({
    packageId = null,
    extraParams = {}
  } = {}) {
    const qstring = getQStringValue({
      extraParams
    });
    const {
      plid,
      sapiHost,
      paths
    } = this;
    const baseUrl = `${sapiHost}${paths.sapiCartPackages}`.replace('{privateLabelId}', plid);
    if (packageId) {
      return `${baseUrl}/${packageId}${qstring}`;
    }
    return `${baseUrl}${qstring}`;
  }

  /**
   * getGenAiLogoBatchGenerationUrl returns the batchLogoGeneration url using instantiated env
   * @returns {string} full batchLogoGeneration url
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const logoGenUrl = domainServices.getGenAiLogoBatchGenerationUrl();
   */
  getGenAiLogoBatchGenerationUrl(batchId) {
    const {
      genAiBatchHost,
      paths
    } = this;
    const path = paths.logoBatchGeneration;
    return batchId ? `${genAiBatchHost}${path}/${batchId}` : `${genAiBatchHost}${path}`;
  }

  /**
   * sapiCartPackages returns the cart packages given a package Id
   * @param {object} args args object containing...
   * @param {string} args.packageId target packageId
   * @param {object} [args.options={}] call options for adding headers, etc.
   * @param {object} [args.extraParams={}] extra parameters that go on the query string
   * @returns {object} JSON object containing the response data
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const packageId = getPackageId();
   * const cartPackageUrl = await domainServices.sapiCartPackages({ packageId });
   */
  async sapiCartPackages({
    packageId = null,
    options = {},
    extraParams = {}
  } = {}) {
    return await this.fetcher.fetch({
      url: this.getSapiCartPackagesUrl({
        packageId,
        extraParams
      }),
      options,
      logger: this.logger
    });
  }

  /**
   * Returns the promise from the post used for the batchLogoGeneration API call
   * @param {object} args args object
   * @param {object} [args.domainNames] required array that contains the domain names to send to batch logo generation API
   * @param {object} [args.iamJwt] required string that contains the iamJwt token to authenticate to the batch logo generation API
   * @param {CallOptions} [args.options] see call options
   * @returns {Promise} returns the Promise for the batchLogoGeneration API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'DPP_SEARCH'});
   * domainServices.genAiLogoBatchGenerationBatchIdPromise({
   *                          domainNames: ['coolexample.org', 'example.org'],
   *                          iamJwt: 'xxxxxx'
   *                        }).then(async (response) => {
   *                          setLogoGenerationResponse(response);
   *                        }).catch(err => console.log(`error occured: ${err}`));
   */
  genAiLogoBatchGenerationBatchIdPromise({
    domainNames = [],
    iamJwt = '',
    options = {}
  } = {}) {
    (0, _utils2.validateParameters)([{
      param: domainNames,
      name: 'domainNames'
    }, {
      param: iamJwt,
      name: 'iamJwt'
    }]);
    return this.fetcher.fetch({
      url: this.getGenAiLogoBatchGenerationUrl(),
      options: {
        ...options,
        method: 'POST',
        headers: {
          'Authorization': `sso-jwt ${iamJwt}`,
          'Content-type': 'application/json'
        },
        body: JSON.stringify({
          requests: domainNames?.reduce((acc, domainName) => {
            acc.push({
              domain_name: domainName,
              requested_logo_generation_count: 1
            });
            return acc;
          }, [])
        })
      },
      logger: this.logger
    });
  }

  /**
   * Returns the promise from the get used for the batchLogoGeneration requestUrl API call
   * @param {object} args args object
   * @param {object} [args.batchId] required string that contains the batchId to send to batch logo generation API for fetching the request urls.
   * @param {object} [args.iamJwt] required string that contains the iamJwt token to authenticate to the batch logo generation API
   * @param {CallOptions} [args.options] see call options
   * @returns {Promise} returns the Promise for the batchLogoGeneration requestUrl API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'DPP_SEARCH'});
   * domainServices.genAiLogoBatchGenerationRequestUrlPromise({
   *                          batchId: '12345',
   *                          iamJwt: 'xxxxxx'
   *                        }).then(async (response) => {
   *                          setLogoGenerationResponse(response);
   *                        }).catch(err => console.log(`error occured: ${err}`));
   */
  genAiLogoBatchGenerationRequestUrlPromise({
    batchId,
    iamJwt = '',
    options = {}
  } = {}) {
    (0, _utils2.validateParameters)([{
      param: batchId,
      name: 'batchId'
    }, {
      param: iamJwt,
      name: 'iamJwt'
    }]);
    return this.fetcher.fetch({
      url: this.getGenAiLogoBatchGenerationUrl(batchId),
      options: {
        ...options,
        method: 'GET',
        headers: {
          Authorization: `sso-jwt ${iamJwt}`
        }
      },
      logger: this.logger
    });
  }

  /**
   * Returns the promise from the get used for the batchLogoGeneration logoUrl API call
   * @param {object} args args object
   * @param {object} [args.logoUrl] required string that contains the logoUrl to send to batch logo generation API for fetching the rendered logo.
   * @param {object} [args.iamJwt] required string that contains the iamJwt token to authenticate to the batch logo generation API
   * @param {CallOptions} [args.options] see call options
   * @returns {Promise} returns the Promise for the batchLogoGeneration API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'DPP_SEARCH'});
   * domainServices.genAiLogoBatchGenerationLogoUrlPromise({
   *                          logoUrl: 'http://somelogoUrl.com',
   *                          iamJwt: 'xxxxxx'
   *                        }).then(async (response) => {
   *                          setLogoGenerationResponse(response);
   *                        }).catch(err => console.log(`error occured: ${err}`));
   */
  genAiLogoBatchGenerationLogoUrlPromise({
    logoUrl,
    iamJwt = '',
    options = {}
  } = {}) {
    (0, _utils2.validateParameters)([{
      param: logoUrl,
      name: 'logoUrl'
    }, {
      param: iamJwt,
      name: 'iamJwt'
    }]);
    return this.fetcher.fetch({
      url: logoUrl,
      options: {
        ...options,
        method: 'GET',
        headers: {
          Authorization: `sso-jwt ${iamJwt}`
        }
      },
      logger: this.logger
    });
  }
  /**
   * Returns the promise from the post used for the logoGeneration API call
   * @param {object} args args object
   * @param {object} [args.ventureId] optional string that contains the ventureId to the logo generation API
   * @param {object} [args.domainName] required string that contains the domain name to logo generation API
   * @param {object} [args.iamJwt] required string that contains the iamJwt token to authenticate to the logo generation API
   * @param {CallOptions} [args.options] see call options
   * @returns {Promise} returns the Promise for the logoGeneration API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'DPP_SEARCH'});
   * domainServices.genAiLogoGenerationPromise({
   *                          domainName: 'coolexample.org',
   *                          iamJwt: 'xxxxxx'
   *                        }).then(async (response) => {
   *                          setLogoGenerationResponse(response);
   *                        }).catch(err => console.log(`error occured: ${err}`));
   */
  genAiLogoGenerationPromise({
    ventureId = '',
    domainName = '',
    iamJwt = '',
    options = {}
  } = {}) {
    (0, _utils2.validateParameters)([{
      param: domainName,
      name: 'domainName'
    }, {
      param: iamJwt,
      name: 'iamJwt'
    }]);
    return this.fetcher.fetch({
      url: this.getGenAiLogoGenerationUrl(),
      options: {
        ...options,
        method: 'POST',
        headers: {
          'Authorization': `sso-jwt ${iamJwt}`,
          'Content-type': 'application/json'
        },
        body: JSON.stringify({
          ...(ventureId && {
            venture_id: ventureId
          }),
          domain_name: domainName
        })
      },
      logger: this.logger
    });
  }

  /**
   * Returns the promise used for the Tailor Brand LLC checker API call
   * @param {object} args args object
   * @param {object} [args.domainName] optional string that contains the domain name used for the LLC availability check
   * @param {object} [args.geoRegionStateCode] optional string that contains the two letter state code used for the LLC availability check
   * @param {object} [args.accessToken] required string that contains the access token for the Tailor Brand api
   * @param {CallOptions} [args.options] see call options
   * @returns {Promise} returns the Promise for the Tailor Brand LLC checker API call
   */
  llcAvailCheckPromise({
    domainName,
    geoRegionStateCode,
    accessToken
  }) {
    (0, _utils2.validateParameters)([{
      param: domainName,
      name: 'domainName'
    }, {
      param: geoRegionStateCode,
      name: 'geoRegionStateCode'
    }, {
      param: accessToken,
      name: 'accessToken'
    }]);
    const params = {
      'filter[business_name]': domainName,
      'filter[state]': geoRegionStateCode
    };
    return this.fetcher.fetch({
      url: this.getLLCAvailCheckUrl({
        params
      }),
      options: {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/vnd.api+json'
        }
      },
      logger: this.logger
    });
  }

  /**
   * Returns the Promise resulting from the fetch call used for the sapiCartPackages API call
   * @param {object} args args object containing...
   * @param {string} args.packageId target packageId
   * @param {object} [args.options={}] call options for adding headers, etc.
   * @param {object} [args.extraParams={}] extra parameters that go on the query string
   * @returns {Promise} returns the promise from fetch used for the sapiCartPackages API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const sapiCartPackagesPromise = domainServices.sapiCartPackagesPromise('domainwanted.com');
   * sapiCartPackagesPromise.then(async (response) => {
   *  const json = await response.json();
   *  setSapiCartPackages(json);
   * }).catch(err => console.log(`error occured: ${err}`))
   */
  sapiCartPackagesPromise({
    packageId = null,
    options = {},
    extraParams = {}
  } = {}) {
    return this.fetcher.fetchPromise({
      url: this.getSapiCartPackagesUrl({
        packageId,
        extraParams
      }),
      options,
      logger: this.logger
    });
  }

  /**
   * getBasketUrl returns the basket url using instantiated env
   * @param {object} args args object containing...
   * @param {string} args.extraParams extra parameters that go on the query string
   * @returns {string} full basket url
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const basketUrl = domainServices.getBasketUrl();
   */
  getBasketUrl({
    extraParams = {}
  } = {}) {
    const {
      plid,
      sapiHost,
      paths
    } = this;
    const qstring = getQStringValue({
      extraParams
    });
    const path = paths.basketGet.replace('{privateLabelId}', plid);
    return `${sapiHost}${path}${qstring}`;
  }

  /**
   * getBasket returns the basket data
   * @param {object} args args object containing...
   * @param {object} [args.options={}] call options for adding headers, etc.
   * @param {object} [args.extraParams={}] extra parameters that go on the query string
   * @returns {object} basket data from call response
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const cartUrl = await domainServices.getBasket();
   */
  async getBasket({
    options = {},
    extraParams = {}
  } = {}) {
    return await this.fetcher.fetch({
      url: this.getBasketUrl({
        extraParams
      }),
      options,
      logger: this.logger
    });
  }

  /**
   * getSapiCartDomainsUrl returns the SAPI cart domains url using instantiated env
   * @param {object} args args object containing...
   * @param {object} [args.options={}] call options for adding headers, etc.
   * @param {object} [args.extraParams={}] extra parameters that go on the query string
   * @returns {string} full cart domains url
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const cartDomains = domainServices.getSapiCartDomainsUrl();
   */
  getSapiCartDomainsUrl({
    domain = '',
    extraParams = {}
  } = {}) {
    const {
      plid,
      sapiHost,
      paths
    } = this;
    const qstring = getQStringValue({
      extraParams
    });
    const path = paths.sapiCartDomains.replace('{privateLabelId}', plid);
    return `${sapiHost}${path}/${domain}${qstring}`;
  }

  /**
   * getGenAiLogoGenerationUrl returns the genAiLogoGeneration url using instantiated env
   * @returns {string} full logoGeneration url
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const logoGenUrl = domainServices.getGenAiLogoGenerationUrl();
   */
  getGenAiLogoGenerationUrl() {
    const {
      genAiHost,
      paths
    } = this;
    const path = paths.logoGeneration;
    return `${genAiHost}${path}`;
  }

  /**
   * Returns the FindAftermarketAuctions API url when provided with extra Params
   * @param {object} args args object containing...
   * @param {object} [args.extraParams] Record<string,string> containing key/values used for querystring params
   * @returns {string} full url for FindAftermarketAuctions API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const getFindAftermarketAuctionsUrl = domainServices.getFindAftermarketAuctionsUrl({ extraParams: {query: 'sometestdomain.com', typeIncludeList: '15'} });
   * const response = await window.fetch(getFindAftermarketAuctionsUrl);
   */
  getFindAftermarketAuctionsUrl({
    extraParams = {}
  } = {}) {
    const {
      findAftermarketHost,
      paths
    } = this;
    const path = paths.afterMarketAuctionRecommend;
    const qString = getQStringValue({
      extraParams
    });
    return `${findAftermarketHost}${path}${qString}`;
  }

  /**
   * getLLCAvailCheckUrl returns the llcAvailCheckUrl url
   * @returns {string} full Tailor Brand LLC Checker api url
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const llcAvailCheckUrl = domainServices.getLLCAvailCheckUrl({ params: { 'filter[business_name]': 'somedomainname.com', 'filter[state]': 'AZ' } });
   */
  getLLCAvailCheckUrl({
    params
  }) {
    const {
      llcAvailCheckApiHost,
      paths
    } = this;
    const qstring = getQStringValue({
      extraParams: params
    });
    const path = paths.llcAvailCheck;
    return `${llcAvailCheckApiHost}${path}${qstring}`;
  }

  /**
   * sapiCartDomains returns results from the SAPI cart domains call
   * @param {object} args args object containing...
   * @param {object} args.domain the target domain
   * @param {object} [args.options={}] call options for adding headers, etc.
   * @param {object} [args.extraParams={}] extra parameters that go on the query string
   * @returns {object} data from call response
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const cartDomains = await domainServices.sapiCartDomains({ domain: 'yourdomain.com' });
   */
  async sapiCartDomains({
    domain = '',
    options = {},
    extraParams = {}
  } = {}) {
    return await this.fetcher.fetch({
      url: this.getSapiCartDomainsUrl({
        domain,
        extraParams
      }),
      options,
      logger: this.logger
    });
  }

  /**
   * Returns the Promise resulting from the fetch call used for the sapiCartDomains API call
   * @param {object} args args object containing...
   * @param {object} args.domain the target domain
   * @param {object} [args.options={}] call options for adding headers, etc.
   * @param {object} [args.extraParams={}] extra parameters that go on the query string
   * @returns {Promise} returns the promise from fetch used for the sapiCartDomains API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const sapiCartDomainsPromise = domainServices.sapiCartDomainsPromise({ domain: 'domainwanted.com' });
   * sapiCartDomainsPromise.then(async (response) => {
   *  const json = await response.json();
   *  setSapiCartDomains(json);
   * }).catch(err => console.log(`error occured: ${err}`))
   */
  sapiCartDomainsPromise({
    domain = '',
    options = {},
    extraParams = {}
  } = {}) {
    return this.fetcher.fetchPromise({
      url: this.getSapiCartDomainsUrl({
        domain,
        extraParams
      }),
      options,
      logger: this.logger
    });
  }

  /**
   * Returns the Promise resulting from the fetch call used for the findAftermarketAuctions API call
   * @param {object} args args object containing...
   * @param {object} [args.options={}] call options for adding headers, etc.
   * @param {object} [args.extraParams={}] extra parameters that go on the query string
   * @param {object} [args.iamJwt] required iam jwt token
   * @returns {Promise} returns the promise from fetch used for the findAftermarketAuctions API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const findAftermarketAuctionsPromise = domainServices.findAftermarketAuctions({extraParams: {query: 'sometestdomain.com', typeIncludeList: '15'}, iamJwt: 'xxxx' });
   * findAftermarketAuctionsPromise.then(async (response) => {
   *  setFindAftermarketAuctionsResponse(response);;
   * }).catch(err => console.log(`error occured: ${err}`))
   */
  findAftermarketAuctionsPromise({
    options = {},
    extraParams = {},
    iamJwt
  } = {}) {
    (0, _utils2.validateParameters)([{
      param: iamJwt,
      name: 'iamJwt'
    }]);
    return this.fetcher.fetch({
      url: this.getFindAftermarketAuctionsUrl({
        extraParams
      }),
      options: {
        ...options,
        method: 'GET',
        headers: {
          'Authorization': `sso-jwt ${iamJwt}`,
          'X-GDFindAM-APIKey': this.apiKey
        }
      },
      logger: this.logger
    });
  }

  /**
   * getContentUrl returns the find api content url using instance environments
   * @param {object} args args object containing...
   * @param {object} args.options call options for adding headers, etc.
   * @param {string} args.extraParams extra parameters that go on the query string
   * @returns {object} content url
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const cartDomains = domainServices.getSapiCartDomainsUrl();
   */
  getContentUrl({
    extraParams = {}
  } = {}) {
    const {
      host,
      paths,
      isReseller,
      plid
    } = this;
    if (isReseller) {
      extraParams.plid = plid;
    }
    extraParams.format = 'json';
    if (this.env !== 'prod') {
      extraParams.contextState = 'draft';
    }
    const qstring = getQStringValue({
      extraParams
    });
    return `${host}${paths.contentApi}${qstring}`;
  }

  /**
   * getContent returns the data from the contentApi response using instance environments
   * @param {object} args args object containing...
   * @param {string} args.extraParams extra parameters that go on the query string
   * @returns {string} full content url
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const contentData = await domainServices.getContent();
   */
  async getContent({
    options = {},
    extraParams = {}
  } = {}) {
    return await this.fetcher.fetch({
      url: this.getContentUrl({
        extraParams
      }),
      options: {
        ...options,
        headers: {
          'X-App-Key': 'serp',
          ...(options.headers || {})
        }
      },
      logger: this.logger
    });
  }

  /**
   * Returns the findExact API url when provided query value and extraParms
   * @param {object} args args object containing...
   * @param {string} args.query domain query value used to search
   * @param {object} [args.extraParams] Record<string,string> that contains key/value pairs for querystring params
   * @returns {string} full url for findExact API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const findExactUrl = domainServices.getExactUrl('domainwanted.com');
   * const response = await window.fetch(findExactUrl);
   */
  getExactUrl({
    query = '',
    extraParams = {}
  } = {}) {
    return this.getUrl({
      query,
      extraParams,
      path: this.paths.exactGet
    });
  }

  /**
   * Returns the find addToCartTrack url when provided extraParms
   * @param {object} args args object containing...
   * @param {object} [args.extraParams] Record<string,string> that contains key/value pairs for querystring params
   * @returns {string} full url for find addToCartTrack call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const findAddToCartUrl = domainServices.getAddToCartTrackUrl();
   * const response = await window.fetch(findAddToCartUrl);
   */
  getAddToCartTrackUrl({
    extraParams = {}
  } = {}) {
    return this.getUrl({
      extraParams,
      path: this.paths.addToCartTrack
    });
  }

  /**
   * Returns the find diviner API url when provided query value and extraParms
   * @param {object} args args object containing...
   * @param {string} args.query domain query value used for the call
   * @param {object} [args.extraParams] Record<string,string> that contains key/value pairs for querystring params
   * @returns {string} full url for find diviner API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const findDivinerUrl = domainServices.getDivinerUrl({});
   * const response = await window.fetch(findDivinerUrl);
   */
  getDivinerUrl({
    query = '',
    extraParams = {}
  } = {}) {
    return this.getUrl({
      query,
      extraParams,
      path: this.paths.divinerGet
    });
  }

  /**
   * Returns the findSpins API url when provided query and extraParms
   * @param {object} args args object containing...
   * @param {string} args.query domain query value used to search
   * @param {object} [args.extraParams] Record<string,string> containing key/values used for querystring params
   * @returns {string} full url for findSpins API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const findSpinsUrl = domainServices.getSpinsUrl('domainwanted.com');
   * const response = await window.fetch(findSpinsUrl);
   */
  getSpinsUrl({
    query = '',
    extraParams = {}
  } = {}) {
    const {
      pagesize = 40,
      pagestart = 0
    } = extraParams;
    const extra = {
      ...extraParams,
      ...{
        pagesize,
        pagestart
      }
    };
    return this.getUrl({
      query,
      extraParams: extra,
      path: this.paths.spinsGet
    });
  }

  /**
   * Returns the find merchRecommend API url when provided query and extraParms
   * @param {object} args args object containing...
   * @param {object} [args.extraParams] Record<string,string> containing key/values used for querystring params
   * @returns {string} full url for merchRecommendUrl API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const merchRecommendUrl = domainServices.getMerchRecommendUrl({
   *   extraParams: {
   *    key: 'merch_api_ads'
   *    user_shopper_id: 1732269
   *   }
   * });
   * const response = await window.fetch(merchRecommendUrl);
   */
  getMerchRecommendUrl({
    extraParams = {}
  } = {}) {
    return this.getUrl({
      extraParams,
      path: this.paths.merchRecommend
    });
  }

  /**
   * Returns the Promise resulting from the fetch call used for the find MerchRecommend API call
   * @param {object} args args object
   * @param {object} [args.extraParams] Record<string,string> that contains key/value pairs for querystring params
   * @param {CallOptions} [args.options] see calloptions
   * @returns {Promise} returns the promise from fetch used for the find MerchRecommend API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'merch_api_ads'});
   * const merchRecommendPromise = domainServices.findMerchRecommendPromise({
   *  Note: All extraParams are optional except user_shopper_id
   *  extraParams: {
   *    user_shopper_id: '2837048',
   *    user_country_site: 'www',
   *    user_visitor_guid: '64e01b7f-58a0-48bf-b1bf-328ff191e3e8',
   *    user_vguid: '',
   *    user_language: 'English',
   *    user_shopper_id: '2837048',
   *    user_isc_code: 'isc',
   *    user_itc_code: 'itc',
   *    domain_source: 'extension',
   *    geo_country_code: 'US',
   *    geo_city_geo_region: 'AZ',
   *    geo_zip_code: '98052',
   *    geo_client_ip: '123.456.789.098',
   *    pagination_size: '10',
   *    server_currency: 'USD',
   *    server_private_label_id: '1'
   *  }
   * });
   * merchRecommendPromise.then(async (response) => {
   *  const json = await response.json();
   *  ...
   * }).catch(err => console.log(`error occured: ${err}`))
   */
  findMerchRecommendPromise({
    extraParams = {},
    options = {}
  } = {}) {
    return this.fetcher.fetchPromise({
      url: this.getMerchRecommendUrl({
        extraParams
      }),
      options,
      logger: this.logger
    });
  }

  /**
   * Returns the json from the findMerchRecommend API call when provided extraParms, and options
   * @param {object} args args object containing...
   * @param {object} [args.extraParams] Record<string,string> that contains key/value pairs for querystring params
   * @param {CallOptions} [args.options] see calloptions
   * @returns {Promise} returns the json from the findMerchRecommend API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const merchRecommendJson = await domainServices.findMerchRecommend({ extraParams: {  user_shopper_id: '2837048' } });
   */
  async findMerchRecommend({
    extraParams = {},
    options = {}
  } = {}) {
    return await this.fetcher.fetch({
      url: this.getMerchRecommendUrl({
        extraParams
      }),
      options,
      logger: this.logger
    });
  }

  /**
   * Returns the crossSell API url when provided query and extraParms
   * @param {object} args args object containing...
   * @param {string} args.query domain query value used to search
   * @param {object} [args.extraParams] Record<string,string> containing key/values used for querystring params
   * @returns {string} full url for findCrossSell API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const findCrossSellUrl = domainServices.getCrossSellUrl('domainwanted.com');
   * const response = await window.fetch(findCrossSellUrl);
   */
  getCrossSellUrl({
    query = '',
    extraParams = {}
  } = {}) {
    const extra = {
      ...extraParams,
      ...{
        sld: query
      }
    };
    return this.getUrl({
      query,
      extraParams: extra,
      path: this.paths.crossSellGet
    });
  }

  /**
   * Returns the findBundle API url when provided query and optionally extraParms
   * @param {object} args args object containing...
   * @param {string} args.query domain query value used to search
   * @param {object} [args.extraParams] Record<string,string> containing key/values used for querystring params
   * @returns {string} full url for findBundles API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const findBundlesUrl = domainServices.getFindBundlesUrl('domainwanted.com');
   * const response = await window.fetch(findBundlesUrl);
   */
  getFindBundlesUrl({
    query = '',
    extraParams = {}
  } = {}) {
    return this.getUrl({
      query,
      extraParams,
      path: this.paths.bundleGet
    });
  }

  /**
   * Returns the smartBundle API url when provided query and optionally extraParms
   * @param {object} args args object containing...
   * @param {string} args.query domain query value used to search
   * @param {object} [args.extraParams] Record<string,string> containing key/values used for querystring params
   * @returns {string} full url for findSmartBundles API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const findSmartBundlesUrl = domainServices.getSmartBundlesUrl('domainwanted.com');
   * const response = await window.fetch(findSmartBundlesUrl);
   */
  getSmartBundlesUrl({
    query = '',
    extraParams = {}
  } = {}) {
    return this.getUrl({
      query,
      extraParams,
      path: this.paths.smartBundleGet
    });
  }

  /**
   * Returns the smartExact API url when provided query and optionally extraParams
   * @param {object} args args object containing...
   * @param {string} args.query domain query value used to search
   * @param {object} [args.extraParams] Record<string,string> containing key/values used for querystring params
   * @returns {string} full url for findSmartExact API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const findSmartExactUrl = domainServices.getSmartExactUrl('domainwanted.com');
   * const response = await window.fetch(findSmartExactUrl);
   */
  getSmartExactUrl({
    query = '',
    extraParams = {}
  } = {}) {
    return this.getUrl({
      query,
      extraParams,
      path: this.paths.smartExactGet
    });
  }

  /**
   * @desc
   * Returns the bulk API url
   * @param {object} args args object containing...
   * @param {object} [args.extraParams] Record<string,string> containing key/values used for querystring params
   * @returns {string} full url for findBulk API call
   * @example
   * const domains = ['neededdomain', 'wanteddomain', 'verycooldomain'];
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const bulkUrl = domainServices.getBulkUrl();
   * // the bulk api call is a POST; domain list goes in the body
   * // the API key is hard-coded by this call to signify a bulk call
   * const response = await window.fetch({
   *   method: 'POST',
   *   credentials: 'include',
   *   body: JSON.stringify({ domains })
   * });
   */
  getBulkUrl({
    extraParams = {}
  } = {}) {
    const {
      paths
    } = this;
    const params = {
      ...extraParams,
      server_private_label_id: this.plid,
      apiKey: extraParams.apiKey || 'bulk_search',
      itc: 'bulk_search',
      fast_check: 'true'
    };
    if (this.isReseller) {
      params.plid = this.plid;
    }
    return this.getUrl({
      query: null,
      // domains are sent in body post
      extraParams: params,
      path: paths.exactMatchGet
    });
  }

  /**
   * Returns the Component BFF API url when extraParms are provided
   * @param {object} args args object containing...
   * @param {object} [args.extraParams] Record<string,string> containing key/values used for querystring params
   * @returns {string} full url for Component BFF API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const componentBFFUrl = domainServices.getDomainSearchComponentBFFUrl({ apiKey: 'TEST_DPP_SEARCH', appName: 'cart', componentName: 'domain-search-box' });
   * const response = await window.fetch(componentBFFUrl);
   */
  getDomainSearchComponentBFFUrl({
    extraParams = {},
    queryParams = {}
  } = {}) {
    return this.getUrl({
      extraParams,
      path: this.paths.getDomainSearchComponentBFFApi,
      keepApiKey: true,
      queryParams
    });
  }

  /**
   * Returns the Domain Suggestions API url with provided extraParms and queryParams
   * @param {object} args args object containing...
   * @param {object} [args.extraParams] Record<string,string> containing key/values used for querystring params
   * @param {string} [args.customerId] string containing the customerId to use in the url
   * @returns {string} full url for Domain Suggestions API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const domainSuggestionsUrl = domainServices.getDomainSuggestionsUrl({ extraParams: { q: 'domainwanted.com' } });
   * const response = await window.fetch(domainSuggestionsUrl);
   */
  getDomainSuggestionsUrl({
    extraParams = {},
    customerId = ''
  } = {}) {
    const {
      paths,
      dccDomainsHost
    } = this;
    const path = paths.domainSuggestions.replace('{customerId}', customerId);
    const qstring = getQStringValue({
      extraParams
    });
    return `${dccDomainsHost}${path}${qstring}`;
  }

  /**
   * Returns the dcc domains get API url
   * @param {object} args args object containing...
   * @param {string} [args.customerId] string containing the customerId to use in the url
   * @returns {string} full url for Domain Suggestions API call
   */
  getDccDomainsGetUrl({
    customerId = ''
  } = {}) {
    const {
      paths,
      dccDomainsHost
    } = this;
    const path = paths.dccDomainsGet.replace('{customerId}', customerId);
    return `${dccDomainsHost}${path}`;
  }

  /**
   * Returns the filter for a dcc domains get POST call. Edit this function to expost more values to the filter as they are needed
   * @param {object} args args object containing...
   * @param {number} args.page page number to return
   * @param {number} args.pageSize number of items to return
   * @param {object} args.filter filter object
   * @param {object} args.domainNamesFilter domainNames filter object
   * @param {string} args.domainNamesFilter.type 'INCLUDE' or 'EXCLUDE'
   * @param {string[]} args.domainNamesFilter.names array of domain names to include or exclude
   * @param {string[]} args.returnColumns array of columns to return
   * @param {string[]} args.domainStates array of domain states to include
   * @param {string[]} args.additionalFields paging direction to use
   * @returns {object} domains get request body
   */
  getDccDomainsGetBody = ({
    page = 1,
    pageSize = 1,
    pagingDirection = 'forward',
    sortColumn = 'domainName',
    sortDirection = 'ascending',
    filter = {},
    domainNamesFilter,
    returnColumns = ['NAME'],
    domainStates = ['DCC_ACTIVE_REGISTERED_DOMAINS', 'REDEMPTION'],
    additionalFields = []
  } = {}) => {
    return {
      pagination: {
        page,
        pageSize,
        pagingDirection,
        sortColumn,
        sortDirection,
        filter: {
          domainStates,
          domainNamesFilter,
          ...filter
        },
        includeEligibility: false,
        includeMarker: false,
        includeRenewalPrices: false,
        markerExpirationDate: null,
        markerDomainName: null,
        markerCreateDate: null,
        markerValuationSaleAmt: null,
        markerAutoRenew: null,
        markerIsLocked: null,
        markerRegistrationType: null,
        markerNameServers: null,
        markerRecordCreateDate: null,
        markerForwardingUrl: null,
        markerProfileName: null,
        markerProtectionPlan: null,
        markerPrivacyLevel: null,
        markerRenewalPrice: null
      },
      returnColumns,
      additionalFields
    };
  };

  /**
   * Returns the json from the findExact API call when provided query and optionally extraParms, and options
   * @param {object} args args object
   * @param {string} args.query domain query value used to search
   * @param {object} [args.extraParams] Record<string,string> that contains key/value pairs for querystring params
   * @param {CallOptions} [args.options] see calloptions
   * @returns {object} returns the json from the findExact API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const exactJson = await domainServices.findExact('domainwanted.com');
   */
  async findExact({
    query = '',
    extraParams = {},
    options = {}
  } = {}) {
    return await this.fetcher.fetch({
      url: this.getExactUrl({
        query,
        extraParams
      }),
      options,
      logger: this.logger
    });
  }

  /**
   * Returns the Promise resulting from the fetch call used for the findExact API call
   * @param {object} args args object
   * @param {string} args.query domain query value used to search
   * @param {object} [args.extraParams] Record<string,string> that contains key/value pairs for querystring params
   * @param {CallOptions} [args.options] see calloptions
   * @returns {Promise} returns the promise from fetch used for the findExact API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const exactPromise = domainServices.findExactPromise('domainwanted.com');
   * exactPromise.then(async (response) => {
   *  const json = await response.json();
   *  setExactJSON(json);
   * }).catch(err => console.log(`error occured: ${err}`))
   */
  findExactPromise({
    query = '',
    extraParams = {},
    options = {}
  } = {}) {
    return this.fetcher.fetchPromise({
      url: this.getExactUrl({
        query,
        extraParams
      }),
      options,
      logger: this.logger
    });
  }

  /**
   * Returns the json from the find addToCartTrack call when provided type, guid, extraParms, and options
   * @param {object} args args object
   * @param {object} [args.extraParams] Record<string,string> that contains key/value pairs for querystring params
   * @param {CallOptions} [args.options] see calloptions
   * @returns {object} returns the json from the addToCartTrack API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const responseJson = await domainServices.findAddToCartTrack();
   */
  async findAddToCartTrack({
    extraParams = {},
    options = {}
  } = {}) {
    return await this.fetcher.fetch({
      url: this.getAddToCartTrackUrl({
        extraParams
      }),
      options: {
        credentials: 'include',
        ...options
      },
      logger: this.logger
    });
  }

  /**
   * Returns the Promise resulting from the fetch call used for the find diviner API call
   * @param {object} args args object
   * @param {object} [args.extraParams] Record<string,string> that contains key/value pairs for querystring params
   * @param {CallOptions} [args.options] see calloptions
   * @returns {Promise} returns the promise from fetch used for the find diviner API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const divinerPromise = domainServices.findDivinerPromise({
   *                          extraParams: {
   *                            user_country_site: 'en-US',
   *                            visitor: '64e01b7f-58a0-48bf-b1bf-328ff191e3e8',
   *                            user_shopper_id: '2837048',
   *                            site_name: 'Dapsonnet Technologies',
   *                            site_tagline: 'Get Professional Services for your business,
   *                            sub_domain_name: 'dapsonnettechnologies.godaddysites.com,
   *                            site_category: 'informationtechnologycompanies',
   *                            business_address: 'Tempe, Arizona',
   *                            site_created: '2000-09-28',
   *                            phone_number: '6673044011
   *                            }
   *                        });
   *  Note: All params optional except req_id which library includes automatically. One of site_name, site_tagline,
   *  sub_domain_name is required to get recommendations.
   * divinerPromise.then(async (response) => {
   *  const json = await response.json();
   *  setDivinerJSON(json);
   * }).catch(err => console.log(`error occured: ${err}`))
   */
  findDivinerPromise({
    extraParams = {},
    options = {}
  } = {}) {
    return this.fetcher.fetchPromise({
      url: this.getDivinerUrl({
        query: '',
        extraParams
      }),
      options,
      logger: this.logger
    });
  }

  /**
   * Returns the promise resulting from the fetch call used for the get domains suggestions API call
   * @param {object} args args object
   * @param {object} [args.extraParams] Record<string,string> that contains key/value pairs for querystring params
   * @param {CallOptions} [args.options] see call options
   * @param {string} [args.customerId] string containing the customerId to use in the url
   * @returns {Promise} returns the promise from fetch used for the get domains suggestions API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'DPP_SEARCH'});
   * domainServices.getDomainSuggestionsPromise({
   *                         extraParams: { name: 'test', vertical: 'business' },
   *                         customerId: '123456'
   *                      }).then(async (response) => {
   *                         const response = await response.json();
   *                        setDomainSuggestionsResponse(response);
   *                      }).catch(err => console.log(`error occured: ${err}`));
   */
  getDomainSuggestionsPromise({
    extraParams = {},
    options = {},
    customerId = ''
  } = {}) {
    (0, _utils2.validateParameters)([{
      param: customerId,
      name: 'customerId'
    }]);
    return this.fetcher.fetchPromise({
      url: this.getDomainSuggestionsUrl({
        extraParams,
        customerId
      }),
      options: {
        ...options,
        headers: {
          ...(options.headers || {}),
          'X-App-Key': this.apiKey
        }
      },
      logger: this.logger
    });
  }

  /**
   *
   * @param {object} args args object
   * @param {string} args.customerId string containing the customerId to use in the url
   * @param {object} args.lookupArgs lookupArgs object
   * @param {number} args.lookupArgs.page page number to return
   * @param {number} args.lookupArgs.pageSize number of items to return
   * @param {object} args.lookupArgs.filter filter object
   * @param {object} args.lookupArgs.domainNamesFilter domainNames filter object
   * @param {string} args.lookupArgs.domainNamesFilter.type 'INCLUDE' or 'EXCLUDE'
   * @param {string[]} args.lookupArgs.domainNamesFilter.names array of domain names to include or exclude
   * @param {string[]} args.lookupArgs.returnColumns array of columns to return
   * @param {string[]} args.lookupArgs.domainStates array of domain states to include
   * @param {string[]} args.lookupArgs.additionalFields paging direction to use
   * @param {object} args.options call options for adding headers, etc.
   * @returns {Promise} returns the promise from POST used for the getDccDomainsGetPromise API call
   */
  getDccDomainsGetPromise({
    customerId = '',
    lookupArgs = {},
    options = {}
  } = {}) {
    (0, _utils2.validateParameters)([{
      param: customerId,
      name: 'customerId'
    }]);
    return this.fetcher.fetchPromise({
      url: this.getDccDomainsGetUrl({
        customerId
      }),
      options: {
        ...options,
        headers: {
          ...(options.headers || {}),
          'X-App-Key': this.apiKey,
          'Content-type': 'application/json'
        },
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify(this.getDccDomainsGetBody(lookupArgs))
      },
      logger: this.logger
    });
  }

  /**
   * Returns the json from the findSpins API call when provided query and optionally extraParms, and options
   * @param {object} args args object
   * @param {string} args.query domain query value used to search
   * @param {object} [args.extraParams] Record<string,string> that contains key/value pairs for querystring params
   * @param {CallOptions} [args.options] see calloptions
   * @returns {object} returns the json from the findSpins API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const spinsJson = await domainServices.findSpins('domainwanted.com');
   */
  async findSpins({
    query = '',
    extraParams = {},
    options = {}
  } = {}) {
    return await this.fetcher.fetch({
      url: this.getSpinsUrl({
        query,
        extraParams
      }),
      options,
      logger: this.logger
    });
  }

  /**
   * Returns the Promise resulting from the fetch call used for the findSpins API call
   * @param {object} args args object containing...
   * @param {string} args.query domain query value used to search
   * @param {object} [args.extraParams] Record<string,string> that contains key/value pairs for querystring params
   * @param {CallOptions} [args.options] see calloptions
   * @returns {Promise} returns the promise from fetch used for the findSpins API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const spinsPromise = domainServices.findSpinsPromise('domainwanted.com');
   * spinsPromise.then(async (response) => {
   *  const json = await response.json();
   *  setSpinsJson(json);
   * }).catch(err => console.log(`error occured: ${err}`))
   */
  findSpinsPromise({
    query = '',
    extraParams = {},
    options = {}
  } = {}) {
    return this.fetcher.fetchPromise({
      url: this.getSpinsUrl({
        query,
        extraParams
      }),
      options,
      logger: this.logger
    });
  }

  /**
   * Returns the json from the findCrossSell API call when provided query and optionally extraParms, and options
   * @param {object} args args object containing...
   * @param {string} args.query domain query value used to search
   * @param {object} [args.extraParams] Record<string,string> that contains key/value pairs for querystring params
   * @param {CallOptions} [args.options] see calloptions
   * @returns {Promise} returns the json from the findCrossSell API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const crossSellJson = await domainServices.findCrossSell('domainwanted.com');
   */
  async findCrossSell({
    query = '',
    extraParams = {},
    options = {}
  } = {}) {
    return await this.fetcher.fetch({
      url: this.getCrossSellUrl({
        query,
        extraParams
      }),
      options,
      logger: this.logger
    });
  }

  /**
   * Returns the Promise resulting from the fetch call used for the findCrossSell API call
   * @param {object} args args object containing...
   * @param {string} args.query domain query value used to search
   * @param {object} [args.extraParams] Record<string,string> that contains key/value pairs for querystring params
   * @param {CallOptions} [args.options] see calloptions
   * @returns {Promise} returns the promise from fetch used for the findCrossSell API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const crossSellPromise = domainServices.findCrossSellPromise('domainwanted.com');
   * crossSellPromise.then(async (response) => {
   *  const json = await response.json();
   *  setCrossSellJson(json);
   * }).catch(err => console.log(`error occured: ${err}`))
   */
  findCrossSellPromise({
    query = '',
    extraParams = {},
    options = {}
  } = {}) {
    return this.fetcher.fetchPromise({
      url: this.getCrossSellUrl({
        query,
        extraParams
      }),
      options,
      logger: this.logger
    });
  }

  /**
   * Returns the json from the findBundles API call when provided query and optionally extraParms, and options
   * @param {object} args args object containing...
   * @param {string} args.query domain query value used to search
   * @param {object} [args.extraParams] Record<string,string> that contains key/value pairs for querystring params
   * @param {CallOptions} [args.options] see calloptions
   * @returns {Promise} returns the json from the findBundles API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const findBundleJson = await domainServices.findBundles('domainwanted.com');
   */
  async findBundles({
    query = '',
    extraParams = {},
    options = {}
  } = {}) {
    return await this.fetcher.fetch({
      url: this.getFindBundlesUrl({
        query,
        extraParams
      }),
      options,
      logger: this.logger
    });
  }

  /**
   * Returns the Promise resulting from the fetch call used for the findBundles API call
   * @param {object} args args object containing...
   * @param {string} args.query domain query value used to search
   * @param {object} [args.extraParams] Record<string,string> that contains key/value pairs for querystring params
   * @param {CallOptions} [args.options] see calloptions
   * @returns {Promise} returns the promise from fetch used for the findBundles API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const findBundlesPromise = domainServices.findBundlesPromise('domainwanted.com');
   * findBundlesPromise.then(async (response) => {
   *  const json = await response.json();
   *  setFindBundleJson(json);
   * }).catch(err => console.log(`error occured: ${err}`))
   */
  findBundlesPromise({
    query = '',
    extraParams = {},
    options = {}
  } = {}) {
    return this.fetcher.fetchPromise({
      url: this.getFindBundlesUrl({
        query,
        extraParams
      }),
      options,
      logger: this.logger
    });
  }

  /**
   * Returns the json from the findSmartBundles API call when provided query and optionally extraParms, and options
   * @param {object} args args object containing...
   * @param {string} args.query domain query value used to search
   * @param {object} [args.extraParams] Record<string,string> that contains key/value pairs for querystring params
   * @param {CallOptions} [args.options] see calloptions
   * @returns {Promise} returns the json from the findSmartBundles API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const smartBundleJson = await domainServices.findSmartBundles('domainwanted.com');
   */
  async findSmartBundles({
    query = '',
    extraParams = {},
    options = {}
  } = {}) {
    return await this.fetcher.fetch({
      url: this.getSmartBundlesUrl({
        query,
        extraParams
      }),
      options,
      logger: this.logger
    });
  }

  /**
   * Returns the Promise resulting from the fetch call used for the findSmartBundles API call
   * @param {object} args args object containing...
   * @param {string} args.query domain query value used to search
   * @param {object} [args.extraParams] Record<string,string> that contains key/value pairs for querystring params
   * @param {CallOptions} [args.options] see calloptions
   * @returns {Promise} returns the promise from fetch used for the findSmartBundles API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const smartBundlesPromise = domainServices.findSmartBundlesPromise('domainwanted.com');
   * smartBundlesPromise.then(async (response) => {
   *  const json = await response.json();
   *  setSmartBundleJson(json);
   * }).catch(err => console.log(`error occured: ${err}`))
   */
  findSmartBundlesPromise({
    query = '',
    extraParams = {},
    options = {}
  } = {}) {
    return this.fetcher.fetchPromise({
      url: this.getSmartBundlesUrl({
        query,
        extraParams
      }),
      options,
      logger: this.logger
    });
  }

  /**
   * Returns the Promise resulting from the fetch call used for the findSmartExact API call
   * @param {object} args args object containing...
   * @param {string} args.query domain query value used to search
   * @param {object} [args.extraParams] Record<string,string> that contains key/value pairs for querystring params
   * @param {CallOptions} [args.options] see calloptions
   * @returns {Promise} returns the promise from fetch used for the findSmartExact API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const smartExactPromise = domainServices.findSmartExactPromise('domainwanted.com');
   * smartExactPromise.then(async (response) => {
   *  const json = await response.json();
   *  setSmartExactJson(json);
   * }).catch(err => console.log(`error occured: ${err}`))
   */
  findSmartExactPromise({
    query = '',
    extraParams = {},
    options = {}
  } = {}) {
    return this.fetcher.fetchPromise({
      url: this.getSmartExactUrl({
        query,
        extraParams
      }),
      options,
      logger: this.logger
    });
  }

  /**
   * Utility method used to retrieve the fetch params for posting bulk options.
   * @param {object} args object containing......
   * @param {array} args.domains list of domains to get the bulk results for
   * @param {extraParams} [args.extraParams] Record<string,string> that contains key/value pairs for querystring params
   * @param {CallOptions} [args.options] see calloptions
   * @returns {object} returns a fetch options object used for the request.
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const bulkFetchOptions = domainServices.findBulkOptions({ domains: ['firstdomain.com', 'seconddomain.com'] });
   * const resp = await window.fetch(bulkFetchOptions);
   */
  findBulkOptions({
    domains = [],
    extraParams = {},
    options = {}
  } = {}) {
    return {
      url: this.getBulkUrl({
        extraParams
      }),
      options: {
        ...options,
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify({
          domains
        })
      },
      logger: this.logger
    };
  }

  /**
   * @param {object} args object containing...
   * @param {array} args.domains list of domains to get the bulk results for
   * @param {extraParams} [args.extraParams] Record<string,string> that contains key/value pairs for querystring params
   * @param {CallOptions} [args.options] see calloptions
   * @returns {object} results from the findBulk API response
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const findBulkJson = await domainServices.findBulk(['domainwanted.com', 'anotherdomain.com', 'yetanother.com']);
   */
  async findBulk({
    domains = [],
    extraParams = {},
    options = {}
  } = {}) {
    return await this.fetcher.fetch(this.findBulkOptions({
      domains,
      extraParams,
      options
    }));
  }

  /**
   * @param {object} args object containing...
   * @param {array} args.domains list of domains to get the bulk results for
   * @param {extraParams} [args.extraParams] Record<string,string> that contains key/value pairs for querystring params
   * @param {CallOptions} [args.options] see calloptions
   * @returns {Promise} returns a promise that should be handled by caller
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const findBulkPromise = domainServices.findBulkPromise(['domainwanted.com', 'anotherdomain.com', 'yetanother.com']);
   * findBulkPromise.then(async (response) => {
   *  const json = await response.json();
   *  setFinkBulkJson(json);
   * }).catch(err => console.log(`error occured: ${err}`))
   */
  findBulkPromise({
    domains = [],
    extraParams = {},
    options = {}
  } = {}) {
    return this.fetcher.fetchPromise(this.findBulkOptions({
      domains,
      extraParams,
      options
    }));
  }

  /**
   * getUipApiUrl returns uip api url
   * @param {string} endpoint parameter to specify endpoint: mutate or graphql
   * @returns {string} full uip api url
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'serp'});
   * const uipUrl = domainServices.getUipApiUrl();
   */
  getUipApiUrl(endpoint) {
    const {
      uipHost,
      paths
    } = this;
    const path = paths.uipApi.replace('{endpoint}', endpoint);
    return `${uipHost}${path}`;
  }

  /**
   * Returns the Promise resulting from the fetch call used for uip API call
   * @param {object} args object containing...
   * @param {object} args.inputData object to pass data or query to uip API
   * @param {string} args.endpoint specificies uip url endpoint
   * @param {CallOptions} [args.options] see calloptions
   * @returns {Promise} returns a promise that should be handled by caller
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'serp'});
   * const uipApiPromise = await domainServices.getUipApiPromise();
   */
  getUipApiPromise({
    inputData = {},
    options = {},
    endpoint = ''
  } = {}) {
    return this.fetcher.fetchPromise({
      url: this.getUipApiUrl(endpoint),
      options: {
        ...options,
        method: 'POST',
        credentials: 'include',
        body: JSON.stringify(inputData),
        headers: {
          'Content-type': 'application/json',
          'api-key': this.apiKey
        }
      },
      logger: this.logger
    });
  }

  /**
   * getDeepIndustryUrl returns the deepIndustry API url using instance environments
   * @param {object} args args object containing...
   * @param {string} args.extraParams extra parameters that go on the query string
   * @returns {object} full url for findDeepIndustry API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const deepIndustryUrl = domainServices.getDeepIndustryUrl();
   */
  getDeepIndustryUrl({
    extraParams = {}
  } = {}) {
    return this.getUrl({
      query: null,
      extraParams,
      path: this.paths.deepIndustryGet
    });
  }

  /**
   * @param {object} args object containing...
   * @param {extraParams} [args.extraParams] Record<string,string> that contains key/value pairs for querystring params
   * @returns {Promise} returns a promise that should be handled by caller
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const getDeepIndustryPromise = domainServices.getDeepIndustryPromise();
   * getDeepIndustryPromise.then(async (response) => {
   *  const json = await response.json();
   *  setDeepIndustryJson(json);
   * }).catch(err => console.log(`error occured: ${err}`))
   */
  getDeepIndustryPromise({
    extraParams = {},
    options = {}
  } = {}) {
    return this.fetcher.fetchPromise({
      url: this.getDeepIndustryUrl({
        extraParams
      }),
      options,
      logger: this.logger
    });
  }

  /**
   * getQueryCompleteApiUrl returns the Autocomplete API url
   * @param {string} query query string for the URL
   * @param {string} version version of the API
   * @returns {string} full url for Autocomplete API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const queryCompleteApiUrl = domainServices.getQueryCompleteApiUrl();
   */
  getQueryCompleteApiUrl(query, version = '') {
    return this.getUrl({
      query,
      path: `${this.paths.queryComplete}${version}`
    });
  }

  /**
   * @param {string} query query string for the API URL
   * @param {string} version version of the API
   * @returns {Promise} returns a promise that should be handled by caller
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const queryCompleteApiPromise = domainServices.getQueryCompletePromise();
   * queryCompleteApiPromise.then(async (response) => {
   *  const json = await response.json();
   *  setQueryCompleteJson(json);
   * }).catch(err => console.log(`error occured: ${err}`))
   */
  getQueryCompletePromise(query, version = '') {
    return this.fetcher.fetchPromise({
      url: this.getQueryCompleteApiUrl(query, version),
      includeCorsHeaders: false
    });
  }

  /**
   * Returns the json from the getDomainSearchComponentBFF API call when provided extraParms
   * @param {object} args args object containing...
   * @param {object} [args.extraParams] Record<string,string> that contains key/value pairs for querystring params
   * @returns {Promise} returns the json from the getDomainSearchComponentBFF API call
   * @example
   * const domainServices = new DomainServices({apiKey: 'TEST_DPP_SEARCH'});
   * const overridePropsJson = await domainServices.getDomainSearchComponentBFFApi({ apiKey: 'TEST_DPP_SEARCH', appName: 'cart', componentName: 'domain-search-box' });
   */
  async getDomainSearchComponentBFFApi({
    extraParams = {},
    queryParams = {}
  } = {}) {
    return await this.fetcher.fetch({
      url: this.getDomainSearchComponentBFFUrl({
        extraParams,
        queryParams
      }),
      logger: this.logger
    });
  }

  /**
   * Returns the Promise resulting from the fetch call used for the getDomainSearchComponentBFF API call
   * @param {object} args args object containing...
   * @param {object} [args.extraParams] Record<string,string> that contains key/value pairs for querystring params
   * @returns {Promise} returns the promise from fetch used for the getDomainSearchComponentBFF API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const getDomainSearchComponentBFFApiPromise = domainServices.getDomainSearchComponentBFFApiPromise({ apiKey: 'TEST_DPP_SEARCH', appName: 'cart', componentName: 'domain-search-box' });
   * getDomainSearchComponentBFFApiPromise.then(async (response) => {
   *  const json = await response.json();
   *  setOverrideProps(json);
   * }).catch(err => console.log(`error occured: ${err}`))
   */
  getDomainSearchComponentBFFApiPromise({
    extraParams = {}
  } = {}) {
    return this.fetcher.fetchPromise({
      url: this.getDomainSearchComponentBFFUrl({
        extraParams
      }),
      logger: this.logger
    });
  }

  /**
   * Returns the productGraph API url
   * @returns {string} full url for productGraph API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const productGraphUrl = domainServices.getProductGraphUrl();
   * const response = await window.fetch(productGraphUrl);
   */
  getProductGraphUrl() {
    const {
      paths,
      productGraphHost
    } = this;
    const path = paths.productGraphApi;
    return `${productGraphHost}${path}`;
  }

  /**
   * Returns the Promise resulting from the POST call used for the productGraph API call
   * GitHub Repo: https://github.com/gdcorp-im/product-graph
   * @param {object} args args object containing...
   * @param {CallOptions} [args.options] see calloptions
   * @param {object} args.payloadData object to pass data or query to productGraph API
   * @returns {Promise} returns the promise from POST used for the productGraph API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const productGraphPromise = await domainServices.findProductGraphPromise();
   */
  findProductGraphPromise({
    options = {},
    payloadData = {}
  } = {}) {
    return this.fetcher.fetchPromise({
      url: this.getProductGraphUrl(),
      options: {
        ...options,
        method: 'POST',
        body: JSON.stringify(payloadData),
        headers: {
          ...(options.headers || {}),
          'Content-type': 'application/json',
          'x-app-key': 'serp'
        }
      },
      logger: this.logger
    });
  }

  /**
   * Returns the Token API url
   * @returns {string} full url for Token API call
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const tokenApiUrl = domainServices.getTokenApiUrl();
   * const response = await window.fetch(tokenApiUrl);
   */
  getTokenApiUrl() {
    const {
      paths,
      tokenApiHost
    } = this;
    const path = paths.tokenApi;
    return `${tokenApiHost}${path}`;
  }

  /**
   * Returns the Promise resulting from the POST call used for the Token API call
   * GitHub Repo: https://github.com/gdcorp-site/SWP/blob/main/Swp.WebCore/Controllers/Api/TokenApiController.cs
   * @param {object} args args object containing...
   * @param {array} args.tokens array of string tokens to pass to Token API for parsing
   * @param {object} [args.options={}] call options for adding headers, etc.
   * @returns {Promise} returns the promise from POST used for the Token API call
   * @throws {Error} if tokens array is empty or not provided
   * @example
   * const domainServices = new DomainServices({fetchEntity: window.fetch, apiKey: 'TEST_DPP_SEARCH'});
   * const tokenApiPromise = await domainServices.getTokenApiPromise({
   *   tokens: [
   *     "{{package[tld-xyz].prices[1:YEAR].listPrice[1:MONTH]}}",
   *     "{{package[tld-xyz].prices[1:YEAR].salePrice[1:MONTH]}}"
   *   ]
   * });
   */
  getTokenApiPromise({
    tokens = [],
    options = {}
  } = {}) {
    (0, _utils2.validateParameters)([{
      param: tokens,
      name: 'tokens'
    }]);
    return this.fetcher.fetchPromise({
      url: this.getTokenApiUrl(),
      options: {
        ...options,
        method: 'POST',
        body: JSON.stringify({
          tokens
        }),
        headers: {
          ...(options?.headers || {}),
          'Content-type': 'application/json',
          'x-app-key': this.apiKey
        }
      },
      logger: this.logger
    }).then(async response => {
      // Handle both raw Response objects and pre-parsed mock responses
      if (response.ok === false) {
        const errorData = response.json ? await response.json() : response;
        throw new Error(`Token API error: ${response.status} - ${JSON.stringify(errorData)}`);
      }
      return response;
    });
  }
}

/**
 * options object used when making a call
 * @typedef {Object} CallOptions
 * @property {number} timeout=30000 - milliseconds to wait for a response
 */

/**
 * DomainServices constructur options
 * @typedef {Object} DomainServicesOptions
 * @property {fetch} fetchEntity - instance of fetch or fetch mechanism that follows the fetch spec and interfaces
 * @property {string} apiKey - this ends up on the querystring as 'key' - contact domain-find team for a value
 * @property {string} [env=prod] - valid values are dev, test, prod, default is prod
 * @property {string} [plid=1] - numeric value representing the registrar ('1' is GoDaddy)
 * @property {string} [market=en-US] - string reprenting the market; @example en-US, fr-CA, fr-FR, etc
 * @property {function} [logger=()=>{}] - function that logs are sent to using interface (log) => {}
 */

/**
 * FetcherResult object returned from Fetcher
 * @typedef {Object} FetcherResult
 * @property {function} fetch - function that calls the fetch mechanism with an async/await
 * @property {function} fetchPromise - function that is used to call fetch and return a Promise
 */
exports.DomainServices = DomainServices;